import { useState } from 'react';
import type { AdminApiError, CheckoutOperation, LaunchDetail as LaunchDetailType } from './types';
import {
  canApproveLaunch,
  canCreateInitialCheckout,
  canRegenerateCheckout,
  canDisableAccessCode,
  canEnableAccessCode,
  canRotateAccessCode,
  demoAccessCodeStateLabel,
  hasDemoAccessMetadata,
  isDemoAccessLocked,
  isDemoAccessDisabled,
  isPaidActive,
  launchStatusLabel,
  nextActionLabel,
  paymentLabel,
  subscriptionLabel,
} from './status';
import { normalizeStableDemoUrl, sanitizeDisplayMessage } from '../../lib/adminApi';

interface LaunchDetailProps {
  detail: LaunchDetailType | null;
  loading: boolean;
  error: AdminApiError | null;
  onApprove: (reason: string) => Promise<void>;
  onCreateCheckout?: (operation: CheckoutOperation) => Promise<void>;
  checkoutPending?: boolean;
  checkoutBlockedMessage?: string;
  onRotateAccessCode?: () => Promise<void>;
  rotatePending?: boolean;
  rotateBlockedMessage?: string;
  onDisableAccessCode?: () => Promise<void>;
  disablePending?: boolean;
  disableBlockedMessage?: string;
  onEnableAccessCode?: () => Promise<void>;
  enablePending?: boolean;
  onRefresh: () => void;
  onClose?: () => void;
}

export function LaunchDetail({ detail, loading, error, onApprove, onCreateCheckout, checkoutPending, checkoutBlockedMessage, onRotateAccessCode, rotatePending, rotateBlockedMessage, onDisableAccessCode, disablePending, disableBlockedMessage, onEnableAccessCode, enablePending, onRefresh, onClose }: LaunchDetailProps) {
  const [reason, setReason] = useState('');
  const [approving, setApproving] = useState(false);
  const [stableUrlCopyError, setStableUrlCopyError] = useState<string | null>(null);
  const [showRotateConfirm, setShowRotateConfirm] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [showEnableConfirm, setShowEnableConfirm] = useState(false);
  const [showRegenerateCheckoutConfirm, setShowRegenerateCheckoutConfirm] = useState(false);

  if (loading && !detail) {
    return <div className="loading" data-testid="detail-loading">Loading launch detail…</div>;
  }

  if (!detail) {
    return (
      <div className="panel" data-testid="detail-unavailable">
        {onClose && (
          <div className="overview-back">
            <button className="btn btn--ghost" onClick={onClose} data-testid="detail-back">
              ← Back to overview
            </button>
          </div>
        )}
        {error && error.type !== 'not-found' ? (
          <div className="error-banner" role="alert" data-testid="detail-error">
            <h3>{error.type === 'auth' ? 'Authentication error' : 'Unable to load clinic'}</h3>
            <p>{sanitizeDisplayMessage(error.message)}</p>
          </div>
        ) : (
          <div className="not-found" data-testid="detail-not-found">
            <h3>Launch not found</h3>
            <p>The launch may have been removed or the ID is invalid.</p>
          </div>
        )}
        <button className="btn btn--secondary" onClick={onRefresh} disabled={loading} data-testid="detail-refresh">
          Refresh
        </button>
      </div>
    );
  }

  const handleApprove = async () => {
    setApproving(true);
    try {
      await onApprove(reason);
    } finally {
      setApproving(false);
    }
  };

  const copyStableUrl = async (value: string | undefined) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setStableUrlCopyError(null);
    } catch {
      setStableUrlCopyError('Copy failed. Select the URL manually and copy it.');
    }
  };

  const checkoutAttemptCount = detail.checkout_attempts?.length ?? 0;
  const hasPaymentHistory = Boolean(detail.checkout || checkoutAttemptCount > 0);
  const initialCheckoutEligibility = canCreateInitialCheckout(detail);
  const regenerateCheckoutEligibility = canRegenerateCheckout(detail);
  const stableDemoUrl = normalizeStableDemoUrl(detail.demo?.stable_demo_url);

  const safeHistoryLabel =
    checkoutAttemptCount > 0 ? `${checkoutAttemptCount} safe ${checkoutAttemptCount === 1 ? 'record' : 'records'}` : 'No history yet';

  const checkoutAttemptStatusLabel = (status: string) => {
    switch (status) {
      case 'created':
        return 'Active';
      case 'superseded':
        return 'Superseded';
      case 'expired':
        return 'Expired';
      case 'failed':
        return 'Failed';
      case 'pending':
        return 'Pending';
      case 'unknown':
        return 'Status unavailable';
      default:
        return 'Status unavailable';
    }
  };

  const paymentLinkStateLabel = detail.checkout
    ? checkoutAttemptStatusLabel(detail.checkout.status)
    : checkoutAttemptCount > 0 ? 'History available' : 'Not generated';

  const checkoutAttemptStatusTint = (status: string) => {
    switch (status) {
      case 'created':
      case 'pending':
        return 'warning';
      case 'superseded':
        return 'info';
      case 'failed':
        return 'danger';
      default:
        return '';
    }
  };

  const formatTimestamp = (value?: string) => (value ? new Date(value).toLocaleString() : 'Not provided');
  const formatCompactId = (value: string) => (value.length > 18 ? `${value.slice(0, 10)}...${value.slice(-6)}` : value);

  const renderPaymentLinkAction = () => {
    if (!onCreateCheckout) {
      return null;
    }

    if (hasPaymentHistory) {
      if (checkoutBlockedMessage) {
        return (
          <div className="list-item-meta" data-testid="regenerate-checkout-blocked">
            {checkoutBlockedMessage}
          </div>
        );
      }

      if (!regenerateCheckoutEligibility.allowed) {
        return (
          <div className="list-item-meta" data-testid="regenerate-checkout-disabled">
            Payment link regeneration not available: {regenerateCheckoutEligibility.reason}
          </div>
        );
      }

      if (!showRegenerateCheckoutConfirm) {
        return (
          <div className="actions">
            <button
              className="btn"
              onClick={() => setShowRegenerateCheckoutConfirm(true)}
              disabled={checkoutPending || approving || loading}
              data-testid="regenerate-checkout-button"
            >
              Regenerate payment link
            </button>
          </div>
        );
      }

      return (
        <div className="dismiss-confirm" data-testid="regenerate-checkout-confirm">
          <p>
            Regenerating creates a fresh one-time payment link. Prior payment links stay in safe
            history only and their raw URLs cannot be recovered here.
          </p>
          <button
            className="btn"
            onClick={async () => {
              setShowRegenerateCheckoutConfirm(false);
              await onCreateCheckout('regenerate');
            }}
            disabled={checkoutPending}
            data-testid="confirm-regenerate-checkout"
          >
            {checkoutPending ? 'Regenerating…' : 'Confirm regenerate'}
          </button>
          <button
            className="btn btn--secondary"
            onClick={() => setShowRegenerateCheckoutConfirm(false)}
            disabled={checkoutPending}
            data-testid="cancel-regenerate-checkout"
          >
            Cancel
          </button>
        </div>
      );
    }

    if (initialCheckoutEligibility.allowed) {
      if (checkoutBlockedMessage) {
        return (
          <div className="list-item-meta" data-testid="create-checkout-blocked">
            {checkoutBlockedMessage}
          </div>
        );
      }

      return (
        <div className="actions">
          <button
            className="btn"
            onClick={() => onCreateCheckout('initial')}
            disabled={checkoutPending || approving || loading}
            data-testid="create-checkout-button"
          >
            {checkoutPending ? 'Generating payment link…' : 'Generate payment link'}
          </button>
        </div>
      );
    }

    return (
      <div className="list-item-meta" data-testid="checkout-disabled">
        Payment link generation not available: {initialCheckoutEligibility.reason}
      </div>
    );
  };

  return (
    <div className="panel" data-testid="launch-detail">
      {onClose && (
        <div className="overview-back">
          <button className="btn btn--ghost" onClick={onClose} data-testid="detail-back">
            ← Back to overview
          </button>
        </div>
      )}
      <div className="panel-header">
        <h3>{detail.name}</h3>
        <button className="btn btn--secondary" onClick={onRefresh} disabled={loading} data-testid="detail-refresh">
          Refresh
        </button>
      </div>

      <div className="status-grid" data-testid="detail-status-grid">
        <div className="status-card">
          <h4>Access</h4>
          <p>{detail.demo && !isDemoAccessDisabled(detail.demo) ? 'Enabled' : 'Not enabled'}</p>
        </div>
        <div className="status-card">
          <h4>Payment link</h4>
          <p>{paymentLinkStateLabel}</p>
        </div>
        <div className="status-card">
          <h4>Paid clinic</h4>
          <p>{isPaidActive(detail.next_action, detail.paid_enrollment_enabled) ? 'Active' : 'Not active'}</p>
        </div>
        <div className="status-card">
          <h4>Safe history</h4>
          <p>{safeHistoryLabel}</p>
        </div>
      </div>

      <div className="detail-layout-grid">
        <section className="detail-primary-column">
          <div className="detail-section">
            <div className="overview-section-title">Current clinic state</div>
            <p className="list-item-meta">
              Launch status: {launchStatusLabel(detail.status)} · Next action: {nextActionLabel(detail.next_action)}
            </p>
            <p className="list-item-meta">
              Payment: {paymentLabel(detail.recent_payment?.status)} · Subscription:{' '}
              {subscriptionLabel(detail.subscription?.status)}
            </p>
          </div>

          {stableDemoUrl && (
            <div className="detail-section" data-testid="stable-url-row">
              <div className="form-row">
                <label htmlFor="detail-stable-url">Stable clinic URL</label>
                <p className="copy-fallback">Durable, safe, and copyable from detail.</p>
                <div className="link-field">
                  <input id="detail-stable-url" readOnly value={stableDemoUrl} data-testid="detail-stable-url" />
                  <button
                    className="btn btn--secondary"
                    onClick={() => copyStableUrl(stableDemoUrl)}
                    data-testid="copy-detail-stable-url"
                  >
                    Copy
                  </button>
                  <a
                    className="btn btn--secondary"
                    href={stableDemoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="open-detail-stable-url"
                  >
                    Open
                  </a>
                </div>
              </div>
              {stableUrlCopyError && (
                <div className="copy-fallback" data-testid="stable-url-copy-error">
                  {stableUrlCopyError}
                </div>
              )}
            </div>
          )}

          <div className="detail-section" data-testid="checkout-history-section">
            <div className="overview-section-title">Safe payment link history</div>
            {checkoutAttemptCount > 0 ? (
              <div className="history-table" data-testid="checkout-history-table">
                <div className="history-row history-row--header">
                  <span>Attempt</span>
                  <span>Status</span>
                  <span>Safe detail</span>
                </div>
                {detail.checkout_attempts?.map((attempt) => (
                  <div
                    className="history-row"
                    key={attempt.checkout_attempt_id}
                    data-testid={`checkout-history-row-${attempt.checkout_attempt_id}`}
                  >
                    <span title={attempt.checkout_attempt_id}>
                      {formatCompactId(attempt.checkout_attempt_id)}
                      <span className="history-row-meta">{attempt.created_at ? `Created ${formatTimestamp(attempt.created_at)}` : 'Created time unavailable'}</span>
                    </span>
                    <span>
                      <span
                        className={`status-tag${checkoutAttemptStatusTint(attempt.status) ? ` status-tag--${checkoutAttemptStatusTint(attempt.status)}` : ''}`}
                      >
                        {checkoutAttemptStatusLabel(attempt.status)}
                      </span>
                    </span>
                    <span className="history-safe-detail" title={attempt.superseded_by_attempt_id ?? undefined}>
                      {attempt.superseded_by_attempt_id
                        ? `Superseded by ${formatCompactId(attempt.superseded_by_attempt_id)}`
                        : attempt.superseded_at
                          ? `Superseded ${formatTimestamp(attempt.superseded_at)}`
                          : attempt.expires_at
                            ? `Expires ${formatTimestamp(attempt.expires_at)}`
                            : 'No raw URL'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="copy-fallback" data-testid="checkout-history-empty">
                No payment link history yet. Generate a payment link when the clinic is ready.
              </div>
            )}
          </div>
        </section>

        <aside className="detail-secondary-column">
          {onCreateCheckout ? (
            <div className="detail-action-card" data-testid="payment-link-action-card">
              <div className="overview-section-title">Payment link</div>
              <p className="list-item-meta">
                Durable detail shows safe state only. Raw payment links appear only in the immediate
                one-time result panel.
              </p>
              {renderPaymentLinkAction()}
            </div>
          ) : null}

          {(() => {
            const demo = detail.demo;
            if (!demo || !hasDemoAccessMetadata(demo)) {
              return null;
            }
            const locked = isDemoAccessLocked(demo.locked_until);
            const showSupportDetails = locked || (demo.failed_login_count ?? 0) > 0;
            return (
              <div className="demo-access-section" data-testid="demo-access-section">
                <div className="overview-section-title">Demo access</div>

                <div className="status-grid" data-testid="demo-access-status-grid">
                  <div className="status-card">
                    <h4>Access code</h4>
                    <p>
                      <span
                        className={`status-tag ${isDemoAccessDisabled(demo) ? 'status-tag--danger' : 'status-tag--success'}`}
                        data-testid="access-code-state-tag"
                      >
                        {demoAccessCodeStateLabel(demo)}
                      </span>
                    </p>
                  </div>
                  {demo.access_code_rotated_at && (
                    <div className="status-card">
                      <h4>Last rotated</h4>
                      <p>{new Date(demo.access_code_rotated_at).toLocaleString()}</p>
                    </div>
                  )}
                  {demo.expires_at && (
                    <div className="status-card">
                      <h4>Demo expires</h4>
                      <p>{new Date(demo.expires_at).toLocaleString()}</p>
                    </div>
                  )}
                </div>

                {locked && (
                  <p className="status-tag status-tag--danger" data-testid="access-code-locked-warning">
                    Locked until {new Date(demo.locked_until as string).toLocaleString()} after repeated failed logins
                  </p>
                )}

                {showSupportDetails && (
                  <p className="list-item-meta" data-testid="demo-access-support-details">
                    Failed login attempts: {demo.failed_login_count ?? 0}
                  </p>
                )}

                {onRotateAccessCode && (
                  <div className="rotate-access-code-action" data-testid="rotate-access-code-action">
                    {(() => {
                      const eligibility = canRotateAccessCode(detail);
                      if (rotateBlockedMessage) {
                        return (
                          <div className="list-item-meta" data-testid="rotate-access-code-blocked">
                            {rotateBlockedMessage}
                          </div>
                        );
                      }
                      if (!eligibility.allowed) {
                        return (
                          <div className="list-item-meta" data-testid="rotate-access-code-disabled">
                            Rotate access code not available: {eligibility.reason}
                          </div>
                        );
                      }
                      if (!showRotateConfirm) {
                        return (
                          <div className="actions">
                            <button
                              className="btn"
                              onClick={() => setShowRotateConfirm(true)}
                              disabled={rotatePending || approving || loading}
                              data-testid="rotate-access-code-button"
                            >
                              Rotate access code
                            </button>
                          </div>
                        );
                      }
                      return (
                        <div className="dismiss-confirm" data-testid="rotate-access-code-confirm">
                          <p>
                            Rotating generates a new access code and invalidates the old code for future
                            logins only. Active demo parent/staff sessions may remain valid until the demo
                            is disabled, expires, or converts to paid. Use Disable to cut off active
                            sessions immediately.
                          </p>
                          <button
                            className="btn"
                            onClick={async () => {
                              setShowRotateConfirm(false);
                              await onRotateAccessCode();
                            }}
                            disabled={rotatePending}
                            data-testid="confirm-rotate-access-code"
                          >
                            {rotatePending ? 'Rotating…' : 'Confirm rotate'}
                          </button>
                          <button
                            className="btn btn--secondary"
                            onClick={() => setShowRotateConfirm(false)}
                            disabled={rotatePending}
                            data-testid="cancel-rotate-access-code"
                          >
                            Cancel
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {onDisableAccessCode && (
                  <div className="disable-access-code-action" data-testid="disable-access-code-action">
                    {(() => {
                      const eligibility = canDisableAccessCode(detail);
                      if (disableBlockedMessage) {
                        return (
                          <div className="list-item-meta" data-testid="disable-access-code-blocked">
                            {disableBlockedMessage}
                          </div>
                        );
                      }
                      if (!eligibility.allowed) {
                        return (
                          <div className="list-item-meta" data-testid="disable-access-code-disabled">
                            Disable access code not available: {eligibility.reason}
                          </div>
                        );
                      }
                      if (!showDisableConfirm) {
                        return (
                          <div className="actions">
                            <button
                              className="btn btn--danger"
                              onClick={() => setShowDisableConfirm(true)}
                              disabled={disablePending || approving || loading}
                              data-testid="disable-access-code-button"
                            >
                              Disable access code
                            </button>
                          </div>
                        );
                      }
                      return (
                        <div className="dismiss-confirm" data-testid="disable-access-code-confirm">
                          <p>
                            Disabling immediately cuts off active demo parent/staff sessions on backend
                            demo-gated routes. Unlike rotate, disable does not wait for a future login,
                            no access code, old or new, will work until access is re-enabled.
                          </p>
                          <button
                            className="btn btn--danger"
                            onClick={async () => {
                              setShowDisableConfirm(false);
                              await onDisableAccessCode();
                            }}
                            disabled={disablePending}
                            data-testid="confirm-disable-access-code"
                          >
                            {disablePending ? 'Disabling…' : 'Confirm disable'}
                          </button>
                          <button
                            className="btn btn--secondary"
                            onClick={() => setShowDisableConfirm(false)}
                            disabled={disablePending}
                            data-testid="cancel-disable-access-code"
                          >
                            Cancel
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {onEnableAccessCode && (
                  <div className="enable-access-code-action" data-testid="enable-access-code-action">
                    {(() => {
                      const eligibility = canEnableAccessCode(detail);
                      if (!eligibility.allowed) {
                        return (
                          <div className="list-item-meta" data-testid="enable-access-code-disabled">
                            Enable access code not available: {eligibility.reason}
                          </div>
                        );
                      }
                      if (!showEnableConfirm) {
                        return (
                          <div className="actions">
                            <button
                              className="btn"
                              onClick={() => setShowEnableConfirm(true)}
                              disabled={enablePending || approving || loading}
                              data-testid="enable-access-code-button"
                            >
                              Enable access code
                            </button>
                          </div>
                        );
                      }
                      return (
                        <div className="dismiss-confirm" data-testid="enable-access-code-confirm">
                          <p>
                            Enabling restores demo access-code login for this launch. This is a recovery
                            action, it does not generate a new raw access code. Rotate intentionally
                            afterward if operators need a fresh code to share.
                          </p>
                          <button
                            className="btn"
                            onClick={async () => {
                              setShowEnableConfirm(false);
                              await onEnableAccessCode();
                            }}
                            disabled={enablePending}
                            data-testid="confirm-enable-access-code"
                          >
                            {enablePending ? 'Enabling…' : 'Confirm enable'}
                          </button>
                          <button
                            className="btn btn--secondary"
                            onClick={() => setShowEnableConfirm(false)}
                            disabled={enablePending}
                            data-testid="cancel-enable-access-code"
                          >
                            Cancel
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })()}
        </aside>
      </div>

      {detail.approved_at && (
        <p className="list-item-meta">
          Approved by {detail.approved_by_email ?? 'unknown'} at{' '}
          {new Date(detail.approved_at).toLocaleString()}
          {detail.approval_reason ? ` — “${detail.approval_reason}”` : ''}
        </p>
      )}

      {error && error.type !== 'not-found' && (
        <div className="error-banner" role="alert" data-testid="detail-error">
          {sanitizeDisplayMessage(error.message)}
        </div>
      )}

      <div className="approve-form" data-testid="approve-form">
        {canApproveLaunch(detail) ? (
          <>
            <div className="form-row">
              <label htmlFor="approve-reason">Approval reason (optional)</label>
              <textarea
                id="approve-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                data-testid="approve-reason"
              />
            </div>
            <div className="actions">
              <button className="btn" onClick={handleApprove} disabled={approving || loading} data-testid="approve-button">
                Approve paid activation
              </button>
            </div>
          </>
        ) : detail.demo ? (
          <div className="list-item-meta" data-testid="approve-disabled">
            Demo paid activation is driven by checkout and subscription lifecycle evidence. Approval is not available for demo launches.
          </div>
        ) : (
          <div className="list-item-meta" data-testid="approve-disabled">
            Approval is not available yet. Current action: {nextActionLabel(detail.next_action)}.
          </div>
        )}
      </div>
    </div>
  );
}
